/**
 * Configuration Manager for application settings and persistence
 */

import type { DataManagerInterface, AppConfig } from '../types';
import { presetFrequencies } from '../features/preset-manager/presets';

class ConfigManager {
    dataManager: DataManagerInterface;
    config: Partial<AppConfig>;

    constructor(dataManager: DataManagerInterface) {
        this.dataManager = dataManager;
        this.config = this._loadConfig();
    }

    /**
     * Get current configuration
     * @returns {AppConfig} Current configuration
     */
    getConfig(): AppConfig {
        return { ...this._getDefaultConfig(), ...(this.config || {}) } as AppConfig;
    }

    /**
     * Get interpolation mode
     * @returns {AppConfig['interpolationMode']} Current interpolation mode
     */
    getInterpolationMode(): AppConfig['interpolationMode'] {
        return this.config.interpolationMode;
    }

    /**
     * Set interpolation mode
     * @param {AppConfig['interpolationMode']} mode - 'log' or 'linear'
     */
    setInterpolationMode(mode: AppConfig['interpolationMode']): void {
        this.config.interpolationMode = mode;
        this._saveConfig();
    }

    /**
     * Get frequency range
     * @returns {Object} Min and max frequency
     */
    getFrequencyRange() {
        return {
            min: this.config.minFreq,
            max: this.config.maxFreq
        };
    }

    /**
     * Set frequency range
     * @param {number} min - Minimum frequency
     * @param {number} max - Maximum frequency
     */
    setFrequencyRange(min: number, max: number): void {
        this.config.minFreq = min;
        this.config.maxFreq = max;
        this._saveConfig();
    }

    /**
     * Update configuration with new values
     * @param {Object} updates - Configuration updates
     */
    updateConfig(updates: Partial<AppConfig>): void {
        Object.assign(this.config, updates);
        this._saveConfig();
    }

    /**
     * Reset configuration to defaults
     */
    resetToDefaults() {
        this.config = this._getDefaultConfig();
        this._saveConfig();
    }

    /**
     * Load configuration from localStorage
     * @private
     */
    _loadConfig() {
        const defaultConfig = this._getDefaultConfig();

        // Load individual settings from localStorage for backwards compatibility
        const interpolationRaw = this.dataManager.loadFromLocalStorage<string>('peaceEqInterpolationMode', defaultConfig.interpolationMode);
        const interpolationMode: AppConfig['interpolationMode'] = interpolationRaw === 'linear' ? 'linear' : 'log';

        const minRaw = this.dataManager.loadFromLocalStorage<string | number>('resultUsageMinFrequency', defaultConfig.minFreq);
        const maxRaw = this.dataManager.loadFromLocalStorage<string | number>('resultUsageMaxFrequency', defaultConfig.maxFreq);

        const minFreq = Number(minRaw) || defaultConfig.minFreq;
        const maxFreq = Number(maxRaw) || defaultConfig.maxFreq;

        // 0 is a valid value for strength (weakest) and peakQuality (narrower),
        // so hydrate with an isFinite check rather than `||`.
        const loadNumeric = (key: string, fallback: number): number => {
            const raw = Number(this.dataManager.loadFromLocalStorage<string | number>(key, fallback));
            return Number.isFinite(raw) ? raw : fallback;
        };
        // Peace's "usage strength" (ResultUsageStrength): applied as 1.25^(strength-3).
        const strength = loadNumeric('resultUsageStrength', defaultConfig.strength);
        // Peace's "Equalization Peak Quality": Q divisor sqrt(2)^(peakQuality-2).
        const peakQuality = loadNumeric('resultUsagePeakQuality', defaultConfig.peakQuality);

        return {
            interpolationMode,
            minFreq,
            maxFreq,
            strength,
            peakQuality
        };
    }

    /**
     * Save configuration to localStorage
     * @private
     */
    _saveConfig() {
        // Save individual settings for backwards compatibility
        this.dataManager.saveToLocalStorage('peaceEqInterpolationMode', this.config.interpolationMode);
        this.dataManager.saveToLocalStorage('resultUsageMinFrequency', this.config.minFreq);
        this.dataManager.saveToLocalStorage('resultUsageMaxFrequency', this.config.maxFreq);
        this.dataManager.saveToLocalStorage('resultUsageStrength', this.config.strength);
        this.dataManager.saveToLocalStorage('resultUsagePeakQuality', this.config.peakQuality);
    }

    /**
     * Get default configuration
     * @private
     */
    _getDefaultConfig(): AppConfig {
        return {
            interpolationMode: 'log',
            resultUsageMinFrequency: 20,
            resultUsageMaxFrequency: 20000,
            baseGainStrength: 1,
            gainStrength: 1,
            strength: 3,
            peakQuality: 2,
            minFreq: 20,
            maxFreq: 20000,
            isoOffset: 0,
            quality: 'normal',
            presetFrequencies: presetFrequencies
        };
    }
}

// ES module export
export { ConfigManager };
export default ConfigManager;
